import { apiEndpoint } from '../config/api';
import { apiFetch } from './api';

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Request failed with status ${response.status}`);
  }
  return body;
}

export async function getTrackingYears(comCode) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-tracking-years/${comCode}`);
  return parseResponse(response);
}

export async function getBuckets(comCode, trackingYear) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-buckets/${comCode}/${trackingYear}`);
  return parseResponse(response);
}

// Review queue: franchise/trackingYears with >= 1 bucket whose maps_to_trackingseason
// disagrees with the audited trackingseason of its assigned pt 1/2 seasons.
export async function getReviewList(params = {}) {
  const qs = new URLSearchParams();
  if (params.minBuckets != null) qs.set('minBuckets', params.minBuckets);
  if (params.fromYear != null) qs.set('fromYear', params.fromYear);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-review-list${suffix}`);
  return parseResponse(response);
}

// Calendar overview matrix: one row per configured Franchise Calendar.
// Returns { calendars: [{ com_code, trackingyear, active_bucket_count }] }. No params. The service
// layer does NOT transform or pivot -- the component pivots into rows/years and applies the
// year-range display policy (omit 2015, show 2016..max, "—" for missing pairs).
export async function getCalendarMatrix() {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-calendar-matrix`);
  return parseResponse(response);
}

export async function updateBucket(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-bucket-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

export async function addBucket(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-bucket-add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// Persist a steward-chosen chronological order. payload:
// { com_code, trackingyear, ordered_season_sequences: [seq, ...] } -- the complete
// ordered list of the calendar's non-Other season_sequence values. A 409 means the
// calendar changed since it was loaded (refetch and retry).
export async function reorderBuckets(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-bucket-reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

export async function deleteBucket(comCode, trackingYear, seasonSequence) {
  const response = await apiFetch(
    `${apiEndpoint}/franchise-season-sequence-bucket-delete/${comCode}/${trackingYear}/${seasonSequence}`,
    { method: 'DELETE' }
  );
  return parseResponse(response);
}

export async function cloneBuckets(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-bucket-clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// Bulk annual roll-forward: generate next year's Franchise Calendars from a source year.
export async function getCalendarYears() {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-calendar-years`);
  return parseResponse(response);
}

export async function getRollForwardPreview(sourceTrackingYear, targetTrackingYear) {
  const qs = new URLSearchParams({ source: sourceTrackingYear, target: targetTrackingYear });
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-roll-forward-preview?${qs.toString()}`);
  return parseResponse(response);
}

export async function applyRollForward(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-sequence-roll-forward`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// AUD-1/AUD-2 — assign an Operational Season to a Franchise Calendar bucket.
// payload: { lksi_rev_and_rollseasonsid, com_code, trackingyear, season_sequence, expected_season_sequence }
// Throws on non-2xx; a 409 body carries { message, status, seasonTrackingSeason, bucketMapsTo } for Mismatch.
export async function assignSeasonToBucket(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-season-assign-bucket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}
